import { useRef, useState } from 'react';
import { Box } from '@material-ui/core';

import {
  PreviewToolbar,
  SCROLL_AREA_PADDING,
  usePreviewStyles,
  useZoom,
  ZoomControls
} from 'componentsV2/pdf-preview-dialog';
import { useElementSize } from 'hooksv2';

import { UnavailableContent } from '../unavailable-content';

interface ImageContentProps {
  url: string;
  name: string;
  onOpenFile: () => void;
}

/** An image with the same zoom toolbar as the PDF preview. At 100% it fits
 *  the canvas; zooming scales it against the canvas width and the canvas
 *  scrolls the overflow. A format the browser cannot show (TIFF, a broken
 *  link) falls back to the "not available" state. */
export const ImageContent = ({ url, name, onOpenFile }: ImageContentProps) => {
  const classes = usePreviewStyles();
  const zoom = useZoom();
  const [failed, setFailed] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { width, height } = useElementSize(scrollAreaRef);

  if (failed) return <UnavailableContent onOpenFile={onOpenFile} />;

  const canvasWidth = width - SCROLL_AREA_PADDING * 2;
  const canvasHeight = height - SCROLL_AREA_PADDING * 2;
  const fitted = zoom.zoom === 1 || !width;

  return (
    <>
      <PreviewToolbar>
        <ZoomControls {...zoom} />
      </PreviewToolbar>

      <div className={classes.scrollArea} ref={scrollAreaRef}>
        <Box className={classes.doc}>
          <img
            src={url}
            alt={name}
            onError={() => setFailed(true)}
            style={{
              display: 'block',
              borderRadius: 8,
              objectFit: 'contain',
              ...(fitted
                ? {
                    maxWidth: '100%',
                    maxHeight: canvasHeight > 0 ? canvasHeight : undefined
                  }
                : {
                    width: Math.round(canvasWidth * zoom.zoom),
                    maxWidth: 'none'
                  })
            }}
          />
        </Box>
      </div>
    </>
  );
};
